import { FC, useEffect, useMemo } from 'react';
import {
  ActivityIndicator,
  Button,
  Chip,
  Divider,
  Icon,
  IconButton,
  Switch,
  Tooltip,
} from '@kontentino/ui';
import { faSidebar } from '@fortawesome/pro-regular-svg-icons/faSidebar';
import { usePostsContainer } from 'components/posts/context';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';
import PostsLayoutContentBarLabels from 'app/modules/posts/components/postsLayout/postsLayoutContentBar/PostsLayoutContentBarLabels';
import PostsViewSwitcher from 'app/modules/posts/components/postsLayout/postsLayoutContentBar/PostsViewSwitcher';
import { getPostsCurrencyBudgets } from 'app/modules/posts/utils/getPostsCurrencyBudgets';
import { useCurrencies } from 'modules/listings/listingsSelectors';
import { useTranslation } from 'react-i18next';
import { useUser } from 'modules/user/userSelector';
import { usePost } from 'modules/post/postSelector';
import { MODULE_STATUS } from 'constants/modules';
import clsx from 'clsx';

const MAXIMUM_BUDGET_CURRENCIES_LIMIT = 3;

const PostsLayoutContentBar: FC = () => {
  const { posts, filterState, changeFilter, meta, inspirations } =
    usePostsContainer();
  const {
    activeView,
    leftPanelOpen,
    postsIdeasActive,
    inspirationsActive,
    isSidePanelOpen,
    toggleSidePanel,
    openPostsPanel,
    openIdeasPanel,
    openInspirationsPanel,
  } = usePostsLayoutState();
  const inspirationsCount = useMemo(
    () => Object.values(inspirations).reduce((sum, arr) => sum + arr.length, 0),
    [inspirations],
  );
  const currencies = useCurrencies();
  const currentUser = useUser();

  useEffect(() => {
    if (currentUser.isClient && postsIdeasActive) {
      openPostsPanel();
    }
  }, [currentUser.isClient, postsIdeasActive, openPostsPanel]);

  const { totalCount, status: postsStatus } = usePost();
  const currencyBudgets = useMemo(
    () => getPostsCurrencyBudgets(posts.allPosts, currencies),
    [posts, currencies],
  );
  const { t } = useTranslation();
  const budgetLabel = [...currencyBudgets]
    .slice(0, MAXIMUM_BUDGET_CURRENCIES_LIMIT)
    .map((budget) => budget.label)
    .join(' • ');
  const moreBudgets = [...currencyBudgets].slice(
    MAXIMUM_BUDGET_CURRENCIES_LIMIT,
  );
  const isAssignedButtonActive = filterState.selectedUserIds.includes(
    currentUser.id,
  );
  const currentUserPostsCount =
    meta?.assignedUser.find((user) => user.id === currentUser.id)?.postCount ||
    0;

  return (
    <div className="tw-h-11 tw-flex tw-items-center tw-gap-2">
      <div
        className="tw-flex tw-items-center tw-gap-2"
        data-name="posts_content-bar-action-buttons"
      >
        <IconButton
          className={clsx('tw-hidden', {
            'lg:tw-flex': activeView !== 'board',
          })}
          variant={isSidePanelOpen ? 'primary' : 'secondary'}
          size="small"
          icon={<Icon icon={faSidebar} />}
          onClick={toggleSidePanel}
          data-name="posts_sidepanel-visibility-toggle-button"
          data-cy="posts_sidepanel-visibility-toggle-button"
        />
        <div
          className={clsx('tw-flex tw-items-center', {
            'tw-hidden': activeView === 'board',
          })}
        >
          <Switch.List>
            <Switch.Tab
              active={leftPanelOpen && isSidePanelOpen}
              onClick={openPostsPanel}
              data-name="posts_side-panel-toggle-button"
              data-cy="posts_side-panel-toggle-button"
              className="tw-px-3"
            >
              {t('posts')}
              <Chip
                variant={leftPanelOpen && isSidePanelOpen ? 'white' : 'grey'}
              >
                {currentUser.isClient
                  ? posts.allPosts.length
                  : posts.postsWithDate.length}
              </Chip>
            </Switch.Tab>
            {!currentUser.isClient && (
              <Switch.Tab
                active={postsIdeasActive && isSidePanelOpen}
                onClick={openIdeasPanel}
                data-name="posts_post-ideas-toggle-button"
                data-cy="posts_post-ideas-toggle-button"
                className="tw-px-3"
              >
                {t('idea_other')}
                <Chip
                  variant={
                    postsIdeasActive && isSidePanelOpen ? 'white' : 'grey'
                  }
                >
                  {posts.postIdeas.length}
                </Chip>
              </Switch.Tab>
            )}
            <Switch.Tab
              active={inspirationsActive && isSidePanelOpen}
              onClick={openInspirationsPanel}
              data-name="posts_inspirations-toggle-button"
              data-cy="posts_inspirations-toggle-button"
              className="tw-px-3"
            >
              {t('inspirations')}
              <Chip
                variant={
                  inspirationsActive && isSidePanelOpen ? 'white' : 'grey'
                }
              >
                {inspirationsCount}
              </Chip>
            </Switch.Tab>
          </Switch.List>
        </div>
        {currentUserPostsCount !== 0 && (
          <Button
            variant={isAssignedButtonActive ? 'primary' : 'secondary'}
            size="small"
            iconAfter={
              <Chip variant={isAssignedButtonActive ? 'white' : 'grey'}>
                {currentUserPostsCount}
              </Chip>
            }
            onClick={() =>
              changeFilter({
                key: 'selectedUserIds',
                value: isAssignedButtonActive ? [] : [currentUser.id],
              })
            }
            data-name="assign_to_me-content_bar-button"
            data-cy="assign_to_me-content_bar-button"
            className="tw-hidden lg:tw-flex"
          >
            {t('assignedToMe')}
          </Button>
        )}
      </div>

      {postsStatus === MODULE_STATUS.Loading && <ActivityIndicator />}
      <div className="tw-ml-auto tw-flex tw-items-center tw-gap-3">
        <div className="tw-text-sm">
          <span className="tw-text-grayscale-100">{t('post_other')}:</span>{' '}
          <span>{totalCount}</span>
        </div>
        <div className="tw-hidden tw-text-sm lg:tw-block">
          <span className="tw-text-grayscale-100">{t('budget')}:</span>{' '}
          <span>{!!budgetLabel.length ? budgetLabel : 0}</span>
          {moreBudgets.length !== 0 && (
            <>
              {' • '}
              <Tooltip
                content={
                  <>
                    {moreBudgets.map((budget) => (
                      <span className="tw-block" key={budget.code}>
                        {budget.label}
                      </span>
                    ))}
                  </>
                }
                placement="bottom"
              >
                <span className="tw-cursor-pointer tw-underline">
                  {t('more')}
                </span>
              </Tooltip>
            </>
          )}
        </div>
        {!!meta?.label.length && (
          <div className="tw-hidden lg:tw-block">
            <Divider orientation="vertical" className="tw-h-[20px]" />
            <PostsLayoutContentBarLabels
              labels={meta.label}
              selectedLabelsIds={filterState.selectedLabels}
              onSelectLabel={(id) =>
                changeFilter({ key: 'selectedLabels', value: id })
              }
            />
          </div>
        )}
        <div className="tw-hidden tw-items-center tw-gap-x-2 sm:tw-flex">
          <Divider orientation="vertical" className="!tw-h-7" />
          <PostsViewSwitcher />
        </div>
      </div>
    </div>
  );
};

export default PostsLayoutContentBar;
